/**
 * Prompt Card Component
 *
 * Displays a summary of a prompt configuration with edit action.
 */

import React from 'react';
import {
  Card,
  Text,
  Badge,
  Group,
  Button,
  Stack,
  Tooltip
} from '@mantine/core';
import {
  IconEdit,
  IconEye,
  IconSparkles,
  IconFileText
} from '@tabler/icons-react';

// Model display configurations
const MODEL_BADGES = {
  'gpt-4o': { color: 'violet', label: 'GPT-4o' },
  'gpt-4o-mini': { color: 'grape', label: 'GPT-4o Mini' },
  'gpt-5-nano': { color: 'teal', label: 'GPT-5 Nano' },
  'gpt-5-mini': { color: 'cyan', label: 'GPT-5 Mini' },
  'claude-sonnet': { color: 'orange', label: 'Claude Sonnet' },
  'claude-opus': { color: 'red', label: 'Claude Opus' },
  'gemini-pro': { color: 'blue', label: 'Gemini Pro' }
};

const PromptCard = ({ prompt, onEdit, canEdit = false }) => {
  const {
    promptName,
    description,
    modelId,
    resolvedFrom,
    version
  } = prompt;

  const isCustomized = resolvedFrom === 'user_customization';
  const modelBadge = MODEL_BADGES[modelId] || { color: 'gray', label: modelId };

  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{
        borderColor: isCustomized ? 'var(--mantine-color-blue-4)' : undefined,
        background: isCustomized ? 'var(--mantine-color-blue-0)' : undefined
      }}
    >
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group gap="xs" mb={4}>
              <IconFileText size={18} stroke={1.5} />
              <Text fw={600} size="sm" lineClamp={1}>
                {promptName}
              </Text>
            </Group>
            <Text size="xs" c="dimmed" lineClamp={2} style={{ minHeight: 32 }}>
              {description || 'No description'}
            </Text>
          </div>
          {isCustomized && (
            <Tooltip label="You have customized this prompt">
              <Badge
                color="blue"
                variant="filled"
                size="xs"
                leftSection={<IconSparkles size={10} />}
              >
                Custom
              </Badge>
            </Tooltip>
          )}
        </Group>

        {/* Model and version */}
        <Group gap="xs">
          <Badge color={modelBadge.color} variant="light" size="sm">
            {modelBadge.label}
          </Badge>
          {version && version > 1 && (
            <Badge color="gray" variant="outline" size="xs">
              v{version}
            </Badge>
          )}
        </Group>

        {/* Actions */}
        <Group mt="auto">
          <Button
            variant={canEdit ? 'light' : 'subtle'}
            size="xs"
            leftSection={canEdit ? <IconEdit size={14} /> : <IconEye size={14} />}
            onClick={onEdit}
            fullWidth
          >
            {canEdit ? 'Edit' : 'View'}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};

export default PromptCard;
